import { hasUnpublishedChanges } from "@/lib/publicationHelpers";
import { portalDisplayStatus, type PortalPublicationRow } from "@/lib/portalPublish";

/**
 * Statut UNIQUE de publication d'un bien (colonne « Statut » des workspaces
 * Publication Location / Vente, décision Thibaud 2026-09-16) : fusion du
 * statut chez nous (`statutPublication`, `sousOption`, modifs non poussées) et
 * de l'état réel côté Immoweb (dernière row `portal_publications`).
 *
 * Ordre = ordre de tri de la colonne.
 */
export const UNIFIED_STATUSES = [
  "brouillon",
  "en_publication",
  "publie",
  "modifs",
  "sous_option",
  "retire",
  "refuse",
  "supprime",
  "a_depublier",
] as const;
export type UnifiedStatus = (typeof UNIFIED_STATUSES)[number];

export type UnifiedLevel = "muted" | "pending" | "success" | "danger" | "warning";
export const UNIFIED_LEVEL: Record<UnifiedStatus, UnifiedLevel> = {
  brouillon: "muted",
  en_publication: "pending",
  publie: "success",
  modifs: "danger",
  sous_option: "warning",
  retire: "muted",
  refuse: "danger",
  supprime: "muted",
  a_depublier: "warning",
};

export interface UnifiedStatusBien {
  statutPublication?: string | null;
  sousOption?: boolean | null;
  updated_at?: string | null;
  last_published_at?: string | null;
  content_updated_at?: string | null;
}

export type UnifiedStatusRow = Pick<PortalPublicationRow, "status" | "last_action" | "created_at">;

/**
 * Priorités, de la plus urgente à la plus calme :
 *  1. « À dépublier » : plus « Publié » chez nous mais annonce encore en ligne
 *     (anomalie, bouton Dépublier dans la fiche) ;
 *  2. « Refusé » : Immoweb a rejeté la dernière demande (publication OU
 *     dépublication), message au survol ;
 *  3. « Supprimé » : annonce effacée chez Immoweb ;
 *  4. « Sous option » : case cochée (annonce dépubliée) ;
 *  5. « Publié » chez nous : en publication (demande en cours) / modifs non
 *     poussées / publié ;
 *  6. « Retiré » (dépublié à la main ou par LOUÉ/VENDU) sinon « Brouillon ».
 */
export function unifiedPublicationStatus(
  bien: UnifiedStatusBien,
  row: UnifiedStatusRow | null | undefined,
): UnifiedStatus {
  const d = portalDisplayStatus(row, bien.statutPublication ?? null);
  if (d.statusKey === "STILL_ONLINE") return "a_depublier";
  if (d.kind === "error") return "refuse";
  if (d.statusKey === "DELETED") return "supprime";
  if (bien.sousOption === true) return "sous_option";
  if (bien.statutPublication === "Publié") {
    const publishing =
      !!row && d.kind === "pending" && (row.last_action === "publish" || row.last_action === "update");
    if (publishing) return "en_publication";
    const stale = hasUnpublishedChanges({
      statutPublication: bien.statutPublication,
      updated_at: bien.updated_at,
      last_published_at: bien.last_published_at,
      content_updated_at: bien.content_updated_at,
    } as Parameters<typeof hasUnpublishedChanges>[0]);
    return stale ? "modifs" : "publie";
  }
  if (bien.statutPublication === "Retiré") return "retire";
  return "brouillon";
}
